import { useQuery } from "@apollo/client";
import type { ChatMessage } from "../ChatBox/types";
import { effortLabel, LLM_MODELS } from "./graphql";

/**
 * Which model answered, and at what reasoning effort, under an answer. From
 * what the server ran (the chat result, or the turn's saved chat context), not
 * the picker, so it stays right after the user switches models. A key the
 * registry no longer lists shows as the key.
 */
export function AnswerModel({ modelKey, effort }: { modelKey: string; effort?: string | null }) {
  const { data } = useQuery(LLM_MODELS, { fetchPolicy: "cache-first" });
  const name = data?.llmModels.find(m => m.key === modelKey)?.displayName ?? modelKey;
  const label = effort ? `${name} · ${effortLabel(effort)}` : name;
  return (
    <div data-testid="answer-model" style={{ fontSize: 10, color: "#999", marginTop: -4 }}>
      {label}
    </div>
  );
}

/** The model and effort a saved turn's chat context recorded, if it recorded any. */
export function answerModelOf(chatContext?: string | null): { modelKey: string; effort: string | null } | null {
  if (!chatContext) return null;
  try {
    const parsed = JSON.parse(chatContext) as { model?: unknown; effort?: unknown };
    return typeof parsed.model === "string"
      ? { modelKey: parsed.model, effort: typeof parsed.effort === "string" ? parsed.effort : null }
      : null;
  } catch {
    // Older rows store free text here; they simply show no model line.
    return null;
  }
}

/** Id prefix of a model line in the chat history. */
export const ANSWER_MODEL_ID = "answer-model-";

/**
 * The history with `label` at the end of the turn `userMessageId` asked:
 * before the next user message, which a queued message can send before this
 * turn's request returns, or at the end when there is none. Unchanged when the
 * turn is no longer in the history (it was replaced meanwhile): the line would
 * name the model under someone else's answer.
 */
export function placeAnswerModel(history: ChatMessage[], userMessageId: string, label: ChatMessage): ChatMessage[] {
  const asked = history.findIndex(m => m.id === userMessageId);
  if (asked < 0) return history;
  const next = history.findIndex((m, i) => i > asked && m.type === "user");
  if (next < 0) return [...history, label];
  return [...history.slice(0, next), label, ...history.slice(next)];
}
